import type { Comment, CommentCreate } from '@entities/common/Comments/comments';
import type { Document, Documents, PaymentVariant } from '@entities/common/commonQueries/types.ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import { getRtkConfig } from '@shared/config';
import { Article } from '@shared/types/articles';
import { Program } from '@shared/types/programs';
import { queryClient } from '@src/api/query-client.ts';

const commonApi = createApi({
  reducerPath: 'commonApi',
  baseQuery: getRtkConfig('/api/v1/common'),
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  endpoints: (build) => ({
    getAllDocuments: build.query<Documents, undefined>({
      query: () => ({
        url: `documents/`,
        method: 'get',
      }),
    }),
    getPaymentVariants: build.query<PaymentVariant[], undefined>({
      query: () => ({
        url: `payments/systems/`,
        method: 'get',
      }),
    }),
    getDocumentById: build.query<Document, string | undefined>({
      query: (id) => `documents/${id}`,
    }),
    getComment: build.query<Comment, string | undefined>({
      query: (id) => ({
        url: `comment/${id}`,
      }),
    }),
    createComment: build.mutation<Comment, CommentCreate>({
      query: (body) => ({
        url: `comment/`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({ article, program }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          if (article) {
            await queryClient.invalidateQueries({ queryKey: ['article', article] });
          }
          if (program) {
            await queryClient.invalidateQueries({ queryKey: ['program', program] });
          }
        } catch {}
      },
    }),
    updateComment: build.mutation<Comment, CommentCreate & { id: string }>({
      query: ({ id, text }) => ({
        url: `comment/${id}/`,
        method: 'PUT',
        body: { text },
      }),
      async onQueryStarted({ article, program }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          if (article) {
            await queryClient.invalidateQueries({ queryKey: ['article', article] });
          }
          if (program) {
            await queryClient.invalidateQueries({ queryKey: ['program', program] });
          }
        } catch {}
      },
    }),
    deleteComment: build.mutation<
      Comment,
      { commentId: string; article?: number; program?: number }
    >({
      query: ({ commentId }) => ({
        url: `comment/${commentId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ commentId, article, program }, { queryFulfilled }) {
        let programPreviousState;
        let articlePreviousState;
        if (article) {
          articlePreviousState = queryClient.getQueryData(['article', article]);
          queryClient.setQueriesData({ queryKey: ['article', article] }, (oldData: Article) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              comments: oldData.comments.filter((el) => el.id !== Number(commentId)),
            };
          });
        }
        if (program) {
          programPreviousState = queryClient.getQueryData(['program', program]);
          queryClient.setQueriesData({ queryKey: ['program', program] }, (oldData: Program) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              comments: oldData.comments.filter((el) => el.id !== Number(commentId)),
            };
          });
        }
        try {
          await queryFulfilled;
          if (article) {
            await queryClient.invalidateQueries({ queryKey: ['article', article] });
          }
          if (program) {
            await queryClient.invalidateQueries({ queryKey: ['program', program] });
          }
        } catch {
          programPreviousState &&
            queryClient.setQueryData(['program', program], programPreviousState);

          articlePreviousState &&
            queryClient.setQueryData(['article', article], programPreviousState);
        }
      },
    }),
  }),
});
export const {
  useGetAllDocumentsQuery,
  useGetPaymentVariantsQuery,
  useGetDocumentByIdQuery,
  useGetCommentQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} = commonApi;

export const commonApiReducer = commonApi.reducer;
export const commonApiPath = commonApi.reducerPath;
export const commonApiMiddleware = commonApi.middleware;
